import React from "react";
import { useAuth } from "@app/auth-client";
import { Box, Button, CircularProgress, Typography } from "@app/ui-kit";

type AuthGateProps = {
  children: React.ReactNode;
};

/** Cognito is the only way in. Unauthenticated visitors see the sign-in card. */
export const AuthGate = ({ children }: AuthGateProps) => {
  const { isLoading, isAuthenticated, login } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          bgcolor: "background.default"
        }}
      >
        <CircularProgress color="secondary" size={28} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            width: "100%",
            backgroundColor: "background.paper",
            border: "1.5px solid",
            borderColor: "divider",
            borderRadius: "10px",
            px: { xs: 4, md: 6 },
            py: { xs: 5, md: 7 },
            textAlign: "left"
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Box
              component="img"
              src="/bkdp/logos/BKDP_Logo.svg"
              alt="Blair Koch Digital Partners"
              sx={{ height: 56, width: "auto", display: "block" }}
            />
          </Box>
          <Typography variant="overline" sx={{ color: "secondary.main", display: "block", mb: 1 }}>
            Client Portal
          </Typography>
          <Typography variant="h4" sx={{ color: "text.primary", mb: 1.5 }}>
            Welcome
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 4, maxWidth: 380 }}>
            Sign in to access your engagement materials, documents, and applications.
          </Typography>
          <Button
            variant="contained"
            onClick={() => login(window.location.pathname + window.location.search)}
          >
            Sign In
          </Button>
          <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 3 }}>
            Access is by invitation. If you were expecting an invitation and have not received
            one, contact Blair Koch Digital Partners.
          </Typography>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};
